import React from 'react';
import { Spinner, useToast } from '@razorpay/blade/components';
import { useUserId } from 'components/EmployeeDetails/BankAccountDetails/hooks/useUserId';
import { getErrorsAsString } from 'components/Kyc/utils';
import useFetchCompensationDetails from 'components/PayDetails/hooks/useFetchCompensationDetails';
import { format } from 'date-fns';
import { SalaryPreviewTable, SalaryPreviewTableData } from './SalaryPreviewTable';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';

export const RearchSalaryStructurePreview = ({ annualCtc }: { annualCtc: Number }) => {
  const userId = useUserId();
  const { isMileStone1Enabled: isRearchEnabled } = useIsFeatureDisabledForRearch();
  const toast = useToast();
  const payrollMonth = format(Date.now(), 'yyyy-MM-dd');
  const { data, isLoading } = useFetchCompensationDetails({
    queryKey: ['getEmployeeCompensationData', userId, payrollMonth],
    userId: String(userId),
    payrollMonth,
    userFilters: {
      includeZeroSalaryUsers: true,
    },
    userRevisions: [
      {
        revisionInfo: [
          {
            effectiveFromMonth: format(Date.now(), 'yyyy-MM'),
            fullCtc: Number(annualCtc) * 100,
            proratedCtc: Number(annualCtc) * 100,
          },
        ],
      },
    ],
    enabled: isRearchEnabled,

    onError: (e) => {
      toast.show({
        content: getErrorsAsString(e),
        color: 'negative',
        duration: 3000,
      });
    },
  });

  const ctcData = data?.ctcBreakdownData;

  if (isLoading) {
    return <Spinner accessibilityLabel="Loading" />;
  }

  if (!ctcData) return null;

  const ctcTableData: SalaryPreviewTableData[] = [
    ...ctcData.fixed.data.map((component) => ({
      id: component.id,
      name: component.componentName,
      monthlyAmount: component.perMonthAmount,
      annualAmount: component.perYearAmount,
    })),
    ...ctcData.otherCtc.data.map((component) => ({
      id: component.id,
      name: component.componentName,
      monthlyAmount: component.perYearAmount / 12,
      annualAmount: component.perYearAmount,
    })),
  ];

  return <SalaryPreviewTable tableData={ctcTableData} />;
};
